/**
 * SEOHead Component - Comprehensive SEO management with react-helmet-async
 * Provides dynamic meta tags, structured data, and Open Graph optimization
 */
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Practice4SAT';
const SITE_URL = 'https://www.practice4sat.com';
const DEFAULT_IMAGE = 'https://www.practice4sat.com/assets/images/logo.png';

/**
 * SEOHead - Dynamic meta tag management for each page
 * @param {string} title - Page title
 * @param {string} description - Meta description (155-160 chars optimal)
 * @param {string} canonical - Canonical URL path (e.g., '/diagnostic-test')
 * @param {string} type - Open Graph type (website, article, product)
 * @param {object} structuredData - JSON-LD structured data
 * @param {string} image - OG image URL
 * @param {boolean} noindex - Set true for pages that shouldn't be indexed
 * @param {object} breadcrumbs - Breadcrumb data for structured data
 */
export default function SEOHead({
  title,
  description,
  canonical = '',
  type = 'website',
  structuredData = null,
  image = DEFAULT_IMAGE,
  noindex = false,
  breadcrumbs = null,
  keywords = ''
}) {
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const fullUrl = `${SITE_URL}${canonical}`;
  
  // Generate breadcrumb structured data
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url ? `${SITE_URL}${item.url}` : undefined
    }))
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-defined SEO configurations for common pages
export const seoConfigs = {
  home: {
    title: 'Practice4SAT',
    description: 'Master the digital SAT with 8 full-length adaptive practice tests, 4,500+ questions with explanations, free diagnostic test, and instant score prediction. Realistic SAT simulator with timed practice, scoring analysis, and personalized study plans.',
    canonical: '/',
    keywords: 'SAT practice test, digital SAT practice test, free SAT practice test, SAT diagnostic test, SAT prep online, adaptive SAT practice test, SAT score predictor, personalized SAT study plan, SAT math practice, SAT reading practice, SAT practice questions with explanations',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Practice4SAT',
      'alternateName': ['Practice4SAT', 'Practice 4 SAT'],
      'url': 'https://www.practice4sat.com',
      'description': 'Comprehensive digital SAT preparation platform with full-length adaptive practice tests, 4,500+ questions with explanations, free diagnostic test, score prediction, and personalized study plans'
    }
  },
  
  diagnosticTest: {
    title: 'Free SAT Diagnostic Test - Instant Score Prediction',
    description: 'Take our free adaptive SAT diagnostic test with instant score prediction. Covers both Reading & Writing and Math. Identify weak areas and get a personalized study plan. No account required.',
    canonical: '/diagnostic-test',
    keywords: 'free SAT diagnostic test, SAT diagnostic test with score prediction, SAT score predictor, predict SAT score, SAT score estimate, free digital SAT practice test online, SAT assessment, SAT level test, SAT practice test with instant score, SAT practice test with scoring analysis',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Quiz',
      'name': 'Free SAT Diagnostic Test',
      'description': 'Adaptive diagnostic test to assess SAT readiness and predict scores',
      'educationalLevel': 'High School',
      'about': {
        '@type': 'Thing',
        'name': 'SAT Reading, Writing & Math'
      },
      'provider': {
        '@type': 'Organization',
        'name': 'Practice4SAT',
        'url': 'https://www.practice4sat.com'
      },
      'isAccessibleForFree': true,
      'timeRequired': 'PT45M'
    },
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Free Diagnostic Test' }
    ]
  },
  
  quickQuiz: {
    title: 'Quick SAT Quiz - Free 5-Minute Digital SAT Practice',
    description: 'Test your SAT skills with a quick 5-minute quiz. Get instant results and score estimates. Covers both Math and Reading & Writing. No signup required.',
    canonical: '/quick-quiz',
    keywords: 'quick SAT quiz, free SAT practice test, SAT practice test with score, SAT score predictor, instant SAT results, digital SAT practice, SAT practice test online',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Quiz',
      'name': 'Quick SAT Quiz',
      'description': '5-minute SAT quiz with instant results',
      'educationalLevel': 'High School',
      'timeRequired': 'PT5M',
      'isAccessibleForFree': true
    },
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Quick Quiz' }
    ]
  },
  
  dashboard: {
    title: 'Dashboard',
    description: 'Track your SAT progress, view test scores, analyze performance trends, and access all practice tests. Monitor your improvement journey.',
    canonical: '/dashboard',
    noindex: true, // User-specific content
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard' }
    ]
  },
  
  practice: {
    title: 'SAT Practice Questions with Explanations - Math, Reading & Writing',
    description: 'Practice SAT with 4,500+ questions organized by topic. Master algebra, advanced math, geometry, reading, writing, and English conventions with step-by-step explanations. Digital SAT practice questions for every skill.',
    canonical: '/practice',
    keywords: 'SAT practice questions, SAT practice with explanations, SAT math practice, SAT reading practice, SAT writing practice, digital SAT practice questions',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Practice Questions' }
    ]
  },
  
  privacyPolicy: {
    title: 'Privacy Policy',
    description: 'Learn how Practice4SAT protects your privacy and handles personal data. Our commitment to data security and user privacy.',
    canonical: '/privacy-policy',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Privacy Policy' }
    ]
  },
  
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with the Practice4SAT team. We\'re here to help with questions about practice tests, your account, or technical support.',
    canonical: '/contact',
    keywords: 'contact Practice4SAT, SAT prep support, SAT practice help',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Contact Us' }
    ]
  },

  satTricks: {
    title: 'SAT Tricks & Strategies - Math and Reading & Writing',
    description: 'Master the SAT with proven tricks and strategies for every Math and Reading & Writing topic. Learn targeted techniques for algebra, geometry, grammar, reading comprehension, and more. Solve problems faster and avoid common mistakes on the digital SAT.',
    canonical: '/sat-tricks',
    keywords: 'SAT tricks, SAT strategies, SAT math tricks, SAT reading strategies, SAT writing strategies, digital SAT tips, SAT problem solving shortcuts',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'SAT Tricks & Strategies' }
    ]
  }
};

// FAQ Schema generator for landing page
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer
    }
  }))
});

// Course Schema for educational content
export const courseSchema = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  'name': 'Digital SAT Preparation - Practice Tests & Study Plan',
  'description': 'Comprehensive digital SAT preparation with 8 full-length adaptive practice tests, 4,500+ practice questions with explanations, score prediction, and personalized study plans',
  'provider': {
    '@type': 'Organization',
    'name': 'Practice4SAT',
    'sameAs': 'https://www.practice4sat.com'
  },
  'hasCourseInstance': {
    '@type': 'CourseInstance',
    'courseMode': 'online',
    'courseWorkload': 'PT20H'
  },
  'offers': {
    '@type': 'Offer',
    'category': 'Full Access (Lifetime)',
    'price': '49',
    'priceCurrency': 'USD',
    'name': 'Full Access (Lifetime)'
  },
  'educationalLevel': 'High School',
  'image': 'https://www.practice4sat.com/assets/images/logo.png',
  'teaches': ['SAT Reading', 'SAT Writing', 'SAT Mathematics', 'Algebra', 'Advanced Math', 'Geometry & Trigonometry', 'Problem Solving & Data Analysis', 'Information and Ideas', 'Craft and Structure', 'Expression of Ideas', 'Standard English Conventions']
};

